import React, { useEffect, useState } from 'react';

import { Toggle } from 'components/CheckboxToggle/types/Toggle';
import { Dates, useDates } from 'context/DateProvider/DateProvider';
import { convertNowToISO } from 'context/DateProvider/utils/convertNowToISO';
import { useFilters } from 'context/FilterProvider/FilterProvider';
import { useSearchParamsProvider } from 'context/SearchParamsProvider/SearchParamsProvider';
import { paramsArePresent } from 'context/SearchParamsProvider/utils/paramsArePresent';
import FrameDetailsEventsChartContainer from 'features/FrameDetails/Containers/FrameDetailsEventsChartContainer';
import { Frame } from 'features/Frames/types';
import MonetisationHealthChart from 'features/Health/components/MonetisationHealthChart';
import MonetisationHealthPercentageBars from 'features/Health/components/MonetisationHealthPercentageBars';
import { percentageBars } from 'features/Health/components/MonetisationHealthPercentageBars/utils/percentageBars';
import { HealthMetrics } from 'features/Health/containers/MonetisationHealthChartContainer/types/healthMetrics';
import { getFilteredData } from 'features/Health/containers/MonetisationHealthChartContainer/utils/getFilteredData';
import { initialDateTimeRange } from 'features/Health/containers/MonetisationHealthChartContainer/utils/initialDateTimeRange';
import { initialHealthMetricsState } from 'features/Health/containers/MonetisationHealthChartContainer/utils/initialHealthMetricsState';
import StatusHistoryChartContainer from 'features/Status/containers/StatusHistoryChartContainer/StatusHistoryChartContainer';
import { StatusData } from 'features/Status/types/statusData';
import useFetchWithToken from 'hooks/useFetchWithToken';
import { FRAMES_API_URL, MONETISATION_API_URL } from 'utils/environmentVariables';
import { getActiveToggles } from 'utils/getActiveToggles';

import useStyles from './MonetisationHealthChartContainer.styles';

type MonetisationHealthChartContainerProps = {
  toggles: Toggle[];
  frame?: Frame;
};

const MonetisationHealthChartContainer = ({
  toggles,
  frame,
}: MonetisationHealthChartContainerProps) => {
  const classes = useStyles();
  const { dates } = useDates();
  const [healthMetricsData, setHealthMetricsData] =
    useState<HealthMetrics>(initialHealthMetricsState);
  const [filteredHealthMetricsData, setFilteredHealthMetricsData] =
    useState<HealthMetrics>(initialHealthMetricsState);
  const [selectedDateTimeRange, setSelectedDateTimeRange] = useState<Dates>(initialDateTimeRange);
  const [statuses, setStatuses] = useState<StatusData[]>([]);

  const { fetchDataWithToken } = useFetchWithToken();
  const { delivery, demand } = filteredHealthMetricsData;
  const { filters } = useFilters();
  const { searchParams } = useSearchParamsProvider();

  const getUrlPrefix = () => {
    if (frame) {
      if (!frame.id) {
        return;
      }
      return `${FRAMES_API_URL}/${frame.id}/monetisation`;
    }
    return MONETISATION_API_URL;
  };

  const pushEndStatusHistoryDataPoints = (data: StatusData[]): StatusData[] => {
    const value = 2;

    const updatedData = [...data];

    updatedData.push({
      value: value,
      state: 'OK',
      time: 0,
    });

    const oneHalfDayInMillis = 129600000;
    const oneHalfDayInFuture = new Date().getTime() + oneHalfDayInMillis;

    updatedData.push({
      value: value,
      state: 'OK',
      time: oneHalfDayInFuture,
    });

    return updateValueFieldOnAllDataPoints(updatedData);
  };

  const updateValueFieldOnAllDataPoints = (data: StatusData[]): StatusData[] => {
    return data.map((d) => {
      return { value: 2, state: d.state, time: new Date(d.time).getTime() };
    });
  };

  useEffect(() => {
    const urlPrefix = getUrlPrefix();
    if (urlPrefix && paramsArePresent(searchParams, 'from', 'to')) {
      const url = `${getUrlPrefix()}?${searchParams.toString()}`;
      fetchDataWithToken<HealthMetrics>(url, (data) => setHealthMetricsData(data));
    }

    if (frame && frame.id) {
      const statusUrl = `${FRAMES_API_URL}/${frame.id}/status?from=${dates.from}&to=${dates.to}`;
      fetchDataWithToken<StatusData[]>(statusUrl, (data) => {
        setStatuses(pushEndStatusHistoryDataPoints(data));
      });
    }
  }, [frame, dates, filters]);

  useEffect(() => {
    if (healthMetricsData) {
      const activeToggles = getActiveToggles(toggles);
      setFilteredHealthMetricsData(getFilteredData(healthMetricsData, activeToggles));
    }
  }, [healthMetricsData, toggles]);

  return (
    <>
      <div className={classes.container}>
        <MonetisationHealthPercentageBars
          percentageBars={percentageBars(filteredHealthMetricsData)}
        />
        <div className={classes.chartContainer}>
          <MonetisationHealthChart
            chartId="monetisationHealthChart"
            chartData={{
              demand: demand.history,
              delivery: delivery.history,
            }}
            minDateTimeRange={convertNowToISO(dates.from)}
            maxDateTimeRange={convertNowToISO(dates.to)}
            enabledSelect={!!frame}
            dateTimeRange={frame && selectedDateTimeRange}
            setDateTimeRange={frame && setSelectedDateTimeRange}
          />
          {frame && (
            <StatusHistoryChartContainer
              frameId={frame.id}
              minDateTimeRange={convertNowToISO(dates.from)}
              maxDateTimeRange={convertNowToISO(dates.to)}
              dateTimeRange={selectedDateTimeRange}
              setDateTimeRange={setSelectedDateTimeRange}
              statuses={statuses}
            />
          )}
          {frame && (
            <FrameDetailsEventsChartContainer
              frameId={frame.id}
              dateTimeRange={selectedDateTimeRange}
              setDateTimeRange={setSelectedDateTimeRange}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default MonetisationHealthChartContainer;
